import { usePeriods } from '@/services/usePeriods';
import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, ShieldAlert, Loader2, Search , ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminCertificateTypes, useCreateCertificateType, useUpdateCertificateType, useDeleteCertificateType, type CertificateType } from "@/services/adminCertificateTypeService";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import toast from "react-hot-toast";

export default function CertificateTypesPage() {
    const { data: periods } = usePeriods();
    const activePeriod = periods?.find((p: any) => p.is_active);
    const [filterPeriod, setFilterPeriod] = useState<string>("all");

    useEffect(() => {
        if (activePeriod && filterPeriod === "all") {
            setFilterPeriod(String(activePeriod.period_id));
        }
    }, [activePeriod, filterPeriod]);
    const [filterSearch, setFilterSearch] = useState<string>("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Fetch Data
    const { data: certificateTypes, isLoading } = useAdminCertificateTypes();

    // Filter Data Local
    const filteredTypes = certificateTypes?.filter((type: CertificateType) => {
        const matchesPeriod = filterPeriod === "all" || String(type.period_id) === filterPeriod;
        if (!matchesPeriod) return false;
        
        if (!filterSearch) return true;
        const s = filterSearch.toLowerCase();
        return (
            type.name?.toLowerCase().includes(s) ||
            type.code?.toLowerCase().includes(s)
        );
    }) || [];


    // Pagination Logic
    const totalItems = filteredTypes.length;
    const totalPages = Math.ceil(totalItems / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentTypes = filteredTypes.slice(startIndex, endIndex);
    // Mutations
    const createType = useCreateCertificateType();
    const updateType = useUpdateCertificateType();
    const deleteType = useDeleteCertificateType();

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        period_id: "",
        name: "",
        code: "",
        source_type: "" as "" | "convention" | "conference" | "speaker" | "mainroom" | "event_guest",
        description: "",
        is_active: true
    });

    const sourceTypeLabels: Record<string, string> = {
        convention: "Konvensi (Peserta)",
        conference: "Konferensi",
        speaker: "Speaker Konferensi",
        mainroom: "Mainroom / Plenary",
        event_guest: "Panitia & Tamu Konvensi",
    };

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const handleOpenModal = (type?: CertificateType) => {
        if (type) {
            setEditingId(type.id);
            setFormData({
                period_id: type.period_id ? String(type.period_id) : (activePeriod ? String(activePeriod.period_id) : ""),
                name: type.name || "",
                code: type.code || "",
                source_type: type.source_type || "",
                description: type.description || "",
                is_active: type.is_active
            });
        } else {
            setEditingId(null);
            setFormData({
                period_id: activePeriod ? String(activePeriod.period_id) : "",
                name: "",
                code: "",
                source_type: "",
                description: "",
                is_active: true
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || !formData.code || !formData.period_id) {
            toast.error("Periode, Nama dan Kode tipe sertifikat wajib diisi.");
            return;
        }

        const payload = {
            period_id: parseInt(formData.period_id),
            name: formData.name,
            code: formData.code,
            source_type: formData.source_type || null,
            description: formData.description,
            is_active: formData.is_active
        };

        try {
            if (editingId) {
                await updateType.mutateAsync({ id: editingId, payload });
                toast.success("Tipe sertifikat berhasil diperbarui!");
            } else {
                await createType.mutateAsync(payload);
                toast.success("Tipe sertifikat berhasil ditambahkan!");
            }
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <ShieldAlert className="w-64 h-64 text-indigo-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <ShieldAlert className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Tipe Sertifikat
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola referensi tipe sertifikat untuk keperluan kegiatan (contoh: PRESENTER, BEST_PAPER).
                        </p>
                    </div>
                </div>

                <Button 
                    onClick={() => handleOpenModal()} 
                    className="relative z-10 h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-600/20"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Tambah Tipe
                </Button>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                {/* Search & Filter */}
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-full sm:w-[320px]">
                        <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl select-none">
                                <SelectValue placeholder="Pilih Periode" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                <SelectItem value="all">Semua Periode</SelectItem>
                                {periods?.map((period: any) => (
                                    <SelectItem key={period.period_id} value={String(period.period_id)}>
                                        {period.name} {period.is_active && "(Aktif)"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="relative flex-1 w-full max-w-sm ml-auto">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari tipe sertifikat..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            className="pl-10 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Tipe & Kode</th>
                                <th className="px-6 py-4 font-semibold">Deskripsi</th>
                                <th className="px-6 py-4 font-semibold text-center w-28">Status</th>
                                <th className="px-6 py-4 font-semibold text-right w-32">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-500" />
                                        <p>Memuat data tipe sertifikat...</p>
                                    </td>
                                </tr>
                            ) : filteredTypes.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        Data tidak ditemukan.
                                    </td>
                                </tr>
                            ) : (
                                currentTypes.map((type, index) => (
                                    <tr key={type.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4 text-center font-medium text-slate-500 dark:text-slate-400">
                                            {startIndex + index + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900 dark:text-white mb-1">{type.name}</div>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <div className="text-xs font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400 px-2.5 py-1 rounded-md inline-flex">
                                                    {type.code}
                                                </div>
                                                {type.source_type && (
                                                    <div className="text-xs font-medium text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2.5 py-1 rounded-md inline-flex">
                                                        {sourceTypeLabels[type.source_type] || type.source_type}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-slate-600 dark:text-slate-300">
                                                {type.description || "-"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${type.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'}`}>
                                                {type.is_active ? 'Aktif' : 'Non-Aktif'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleOpenModal(type)}
                                                    className="h-8 w-8 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => setDeleteConfirmId(type.id)}
                                                    className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/40"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                {totalItems > 0 && !isLoading && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || totalPages === 0}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Tambah/Edit */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {editingId ? "Edit Tipe Sertifikat" : "Tambah Tipe Sertifikat"}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 mt-1.5">
                            Pastikan kode tipe sertifikat unik (misal: PRESENTER, BEST_PAPER).
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Periode <span className="text-red-500">*</span></Label>
                            <Select value={formData.period_id} onValueChange={(val) => setFormData({ ...formData, period_id: val })}>
                                <SelectTrigger className="w-full overflow-hidden h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-left select-none">
                                    <div className="truncate w-full text-left min-w-0">
                                        <SelectValue placeholder="Pilih Periode" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[250px]">
                                    {periods?.map((period: any) => (
                                        <SelectItem key={period.period_id} value={String(period.period_id)} className="whitespace-normal break-words">
                                            {period.name} {period.is_active && "(Aktif)"}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nama Tipe <span className="text-red-500">*</span></Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Misal: Presenter"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="code" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kode Unik <span className="text-red-500">*</span></Label>
                            <Input
                                id="code"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                placeholder="Misal: PRESENTER"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 uppercase"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kategori Sumber Data</Label>
                            <Select
                                value={formData.source_type || "none"}
                                onValueChange={(val) => setFormData({ ...formData, source_type: val === "none" ? "" : (val as typeof formData.source_type) })}
                            >
                                <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl select-none">
                                    <SelectValue placeholder="Pilih kategori (opsional)" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)]">
                                    <SelectItem value="none">Tidak ditentukan</SelectItem>
                                    {Object.entries(sourceTypeLabels).map(([value, label]) => (
                                        <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Menentukan tipe ini muncul sebagai varian dropdown apa saat generate sertifikat (mis. "Panitia & Tamu Konvensi" untuk data dari Kelola Kategori Kartu).
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Deskripsi</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Deskripsi singkat..."
                                className="resize-none h-20 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-bold text-slate-900 dark:text-white">Status Aktif</Label>
                                <p className="text-xs text-slate-500">Tipe sertifikat yang aktif dapat dipilih saat generate sertifikat.</p>
                            </div>
                            <Switch 
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                            />
                        </div>

                        <DialogFooter className="pt-4 mt-6 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="h-11 px-6 rounded-xl">
                                Batal
                            </Button>
                            <Button type="submit" disabled={createType.isPending || updateType.isPending} className="h-11 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700">
                                {(createType.isPending || updateType.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Tipe Sertifikat?</DialogTitle>
                        <DialogDescription>
                            Tindakan ini tidak dapat dibatalkan. Data yang terhapus mungkin memengaruhi sertifikat yang sudah tercetak.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button 
                            variant="destructive" 
                            disabled={deleteType.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteType.mutateAsync(deleteConfirmId);
                                    toast.success("Tipe sertifikat berhasil dihapus!");
                                    setDeleteConfirmId(null);
                                } catch (error) {
                                    toast.error("Gagal menghapus tipe sertifikat.");
                                }
                            }}
                        >
                            {deleteType.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

